import api from './api';
import {
  Organization,
  OrganizationList,
  StrategicGoal,
  StrategicGoalList,
  Initiative,
  InitiativeList,
  Activity,
  ActivityList,
  KPI,
  KPIList,
  KPIValue,
  KPIReadingSchedule,
  KPIReadingScheduleList,
  Notification,
  NotificationList,
  DashboardData,
  DashboardSettings,
  PerformanceData,
  PaginatedResponse,
  User,
  Service,
  ServiceList,
  OrganizationalUnit,
  OrganizationalUnitList,
  Employee,
  UnitGoal,
  UnitGoalList,
  Process,
  ProcessList,
  SubProcess,
  ProcessStep,
  Operation,
  ChangeRequest,
  ChangeRequestList,
  OrganizationComparison,
  DueKPIReading,
} from '../types';

// ============================================================
// Strategy / Executive Dashboard Overview (aggregated across ALL orgs)
// ============================================================
export interface StrategyOverview {
  organizations_count: number;
  all_orgs_active: boolean;

  goals_count: number;
  pending_goals_count: number;
  approved_goals_count: number;

  initiatives_count: number;
  pending_initiatives_count: number;
  approved_initiatives_count: number;

  strategic_kpis_count: number;
  excellent_strategic_kpis: number;
  initiative_kpis_count: number;
  excellent_initiative_kpis: number;
  process_kpis_count: number;
  excellent_process_kpis: number;

  stage_required_progress: number;
  stage_actual_progress: number;
  stage_capacity: number;
  target_percentage: number;
  actual_percentage: number;
  commitment_index: number;

  overall_achievement_rate: number;
  performance_deviation: number | null;
  achievement_improvement: number;
}

// ============================================================
// Organizations API
// ============================================================
export const organizationsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<OrganizationList>>('/organizations/', { params });
    return response.data;
  },

  get: async (id: number, params?: Record<string, any>) => {
    const response = await api.get<Organization>(`/organizations/${id}/`, { params });
    return response.data;
  },

  create: async (data: Partial<Organization>) => {
    const response = await api.post<Organization>('/organizations/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Organization>) => {
    const response = await api.patch<Organization>(`/organizations/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/organizations/${id}/`);
    return response;
  },

  getDashboard: async (id: number, params?: Record<string, any>) => {
    const response = await api.get(`/organizations/${id}/dashboard/`, { params });
    return response.data;
  },

  getKPIs: async (id: number, params?: Record<string, any>) => {
    const response = await api.get<KPIList[]>(`/organizations/${id}/kpis/`, { params });
    return response.data;
  },

  updateVisionMission: async (id: number, data: { vision?: string; mission?: string }) => {
    const response = await api.patch<Organization>(`/organizations/${id}/update_vision_mission/`, data);
    return response.data;
  },

  getComparison: async (params?: { sort_by?: string; order?: 'asc' | 'desc' }) => {
    const response = await api.get<OrganizationComparison[]>('/organizations/comparison/', { params });
    return response.data;
  },

  // Aggregated dashboard overview across ALL organizations (strategy/executive view)
  getOverview: async (params?: { year?: number }) => {
    const response = await api.get<StrategyOverview>('/organizations/overview/', { params });
    return response.data;
  },

  // Get pending approvals for org_executive
  getPendingApprovals: async () => {
    const response = await api.get('/organizations/pending-approvals/');
    return response.data;
  },

  // Batch approve all pending items for a department
  batchApprove: async (departmentId: number) => {
    const response = await api.post(`/organizations/${departmentId}/batch-approve/`);
    return response.data;
  },

  uploadOrgStructure: async (id: number, formData: FormData) => {
    const response = await api.post(`/organizations/${id}/upload-org-structure/`, formData);
    return response.data;
  },

  deleteOrgStructure: async (id: number) => {
    const response = await api.delete(`/organizations/${id}/delete-org-structure/`);
    return response;
  },
};

// ============================================================
// Emirate Plan Goals API (DTS UC-02 step 3 — optional alignment)
// ============================================================
export interface EmiratePlanGoal {
  id: number;
  title: string;
  description?: string;
  code?: string;
  year?: number | null;
  is_active: boolean;
  aligned_count?: number;
  created_at?: string;
  updated_at?: string;
}

export const emiratePlanApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<EmiratePlanGoal>>('/emirate-plan-goals/', { params });
    return response.data;
  },
  get: async (id: number) => {
    const response = await api.get<EmiratePlanGoal>(`/emirate-plan-goals/${id}/`);
    return response.data;
  },
  create: async (data: Partial<EmiratePlanGoal>) => {
    const response = await api.post<EmiratePlanGoal>('/emirate-plan-goals/', data);
    return response.data;
  },
  update: async (id: number, data: Partial<EmiratePlanGoal>) => {
    const response = await api.patch<EmiratePlanGoal>(`/emirate-plan-goals/${id}/`, data);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/emirate-plan-goals/${id}/`);
    return response;
  },
};

// ============================================================
// Partners API (DTS section 2.1 + UC-03 step 7 + FR-27)
// Curated catalog of external partner organizations referenced by initiatives.
// ============================================================
export type PartnerType =
  | 'government'
  | 'private'
  | 'non_profit'
  | 'academic'
  | 'international'
  | 'other';

export interface Partner {
  id: number;
  name: string;
  name_en?: string;
  partner_type: PartnerType;
  partner_type_display?: string;
  description?: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  website?: string;
  logo?: string | null;
  is_active: boolean;
  initiatives_count?: number;
  created_at?: string;
  updated_at?: string;
  created_by_id?: number;
  created_by_details?: { id: number; username: string; full_name: string } | null;
}

export const partnersApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<Partner>>('/partners/', { params });
    return response.data;
  },
  get: async (id: number) => {
    const response = await api.get<Partner>(`/partners/${id}/`);
    return response.data;
  },
  create: async (data: Partial<Partner>) => {
    const response = await api.post<Partner>('/partners/', data);
    return response.data;
  },
  update: async (id: number, data: Partial<Partner>) => {
    const response = await api.patch<Partner>(`/partners/${id}/`, data);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/partners/${id}/`);
    return response;
  },
  // Bulk import (directorate request 2026-05-13). Returns
  // { success, created, updated, skipped, errors[], message }.
  importFromExcel: async (formData: FormData) => {
    const response = await api.post('/partners/import-excel/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  downloadTemplate: async (lang: 'ar' | 'en' = 'ar') => {
    const response = await api.get('/partners/download-template/', {
      params: { lang },
      responseType: 'blob',
    });
    return response;
  },
};

// ============================================================
// Reference Documents API — directorate library (request 2026-05-13)
// Authority-wide PDF/Office reference library. Read is open to every
// authenticated user; write (POST/PATCH/DELETE) is rejected with 403
// for non-Strategy-Team callers by the backend.
// ============================================================

export type ReferenceDocumentCategory = 'plan' | 'manual' | 'policy' | 'other';

export interface ReferenceDocument {
  id: number;
  title_ar: string;
  title_en: string;
  description: string;
  category: ReferenceDocumentCategory;
  category_display?: string;
  file: string;            // absolute URL (built by USE_X_FORWARDED_HOST)
  file_size: number | null;
  file_extension: string | null;
  is_active: boolean;
  uploaded_by_id: number | null;
  uploaded_by_details?: any;
  uploaded_at: string;
  updated_at: string;
  download_count: number;
}

export const referenceDocumentsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<ReferenceDocument>>(
      '/reference-documents/', { params },
    );
    return response.data;
  },
  get: async (id: number) => {
    const response = await api.get<ReferenceDocument>(`/reference-documents/${id}/`);
    return response.data;
  },
  create: async (formData: FormData) => {
    // Multipart because the payload carries the PDF binary alongside
    // the metadata fields. Drop the json header so axios sets the
    // multipart boundary automatically.
    const response = await api.post<ReferenceDocument>(
      '/reference-documents/', formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },
  update: async (id: number, formData: FormData) => {
    const response = await api.patch<ReferenceDocument>(
      `/reference-documents/${id}/`, formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/reference-documents/${id}/`);
    return response;
  },
  // Bumps download_count atomically. Called right after the browser
  // initiates the file download via the file URL — purely
  // informational, never blocks the actual download.
  incrementDownload: async (id: number) => {
    const response = await api.post(`/reference-documents/${id}/increment-download/`);
    return response.data as { download_count: number };
  },
};


// ============================================================
// Calibration Meetings API (DTS UC-06)
// ============================================================

export type CalibrationMeetingStatus = 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
export type CalibrationMeetingType = 'quarterly' | 'half_yearly' | 'three_quarter' | 'year_end';

export interface CalibrationMeeting {
  id: number;
  title: string;
  meeting_type: CalibrationMeetingType;
  meeting_type_display?: string;
  scheduled_date: string;
  agenda: string;
  meeting_notes: string;
  decisions: string;
  invited_units: number[];
  invited_unit_names: string[];
  status: CalibrationMeetingStatus;
  status_display?: string;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  created_by_id: number | null;
  created_by_details?: any;
}

// ============================================================
// Audit Logs API (DTS chapter 9 — sجل التدقيق)
// Backed by adaa_audit.AuditLog at /api/audit/logs/
// Note: audit endpoints are mounted at /api/audit/ (not /api/v1/),
// so we override the URL — see api.ts baseURL.
// ============================================================

export interface AuditLogEntry {
  id: number;
  user_id: number | null;
  user_display: string;
  action: string;
  entity_type: string;
  entity_id: string;
  entity_repr: string;
  message_ar: string;
  message_en: string;
  changes: Record<string, any> | null;
  ip_address: string | null;
  user_agent: string;
  request_method: string;
  request_path: string;
  status_code: number | null;
  response_ms: number | null;
  page: string;
  component: string;
  action_detail: string;
  session_id: string;
  correlation_id: string;
  source_system: string;
  extra: Record<string, any> | null;
  timestamp: string;
}

// ============================================================
// Archives API (DTS chapter 10 — سلة المحذوفات)
// ============================================================

export interface ArchiveItem {
  id: number;
  content_type: string;
  content_type_display: string;
  object_id: number;
  object_title: string;
  organization_name: string;
  object_data: Record<string, any>;
  deleted_by_id: number | null;
  deleted_by_name?: string;
  deleted_at: string;
  expires_at: string;
  is_restored: boolean;
  days_until_expiry: number;
  is_expired: boolean;
  reason?: string;
}

export const archivesApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<ArchiveItem>>('/archives/', { params });
    return response.data;
  },
  get: async (id: number) => {
    const response = await api.get<ArchiveItem>(`/archives/${id}/`);
    return response.data;
  },
  restore: async (id: number) => {
    const response = await api.post<{ message: string; restored_id: number }>(
      `/archives/${id}/restore/`,
    );
    return response.data;
  },
};

export const auditLogsApi = {
  list: async (params?: Record<string, any>) => {
    // The audit router lives outside the /api/v1/ prefix — strip it.
    const response = await api.get<PaginatedResponse<AuditLogEntry>>(
      '/audit/logs/',
      { baseURL: api.defaults.baseURL?.replace('/api/v1', '/api'), params },
    );
    return response.data;
  },
  get: async (id: number) => {
    const response = await api.get<AuditLogEntry>(`/audit/logs/${id}/`, {
      baseURL: api.defaults.baseURL?.replace('/api/v1', '/api'),
    });
    return response.data;
  },
  entityHistory: async (entityType: string, entityId: string | number) => {
    const response = await api.get<PaginatedResponse<AuditLogEntry>>(
      `/audit/logs/entity/${entityType}/${entityId}/`,
      { baseURL: api.defaults.baseURL?.replace('/api/v1', '/api') },
    );
    return response.data;
  },
};

export const calibrationMeetingsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<CalibrationMeeting>>('/calibration-meetings/', { params });
    return response.data;
  },
  get: async (id: number) => {
    const response = await api.get<CalibrationMeeting>(`/calibration-meetings/${id}/`);
    return response.data;
  },
  create: async (data: Partial<CalibrationMeeting>) => {
    const response = await api.post<CalibrationMeeting>('/calibration-meetings/', data);
    return response.data;
  },
  update: async (id: number, data: Partial<CalibrationMeeting>) => {
    const response = await api.patch<CalibrationMeeting>(`/calibration-meetings/${id}/`, data);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/calibration-meetings/${id}/`);
    return response;
  },
  start: async (id: number) => {
    const response = await api.post<CalibrationMeeting>(`/calibration-meetings/${id}/start/`);
    return response.data;
  },
  complete: async (id: number) => {
    const response = await api.post<CalibrationMeeting>(`/calibration-meetings/${id}/complete/`);
    return response.data;
  },
  cancel: async (id: number) => {
    const response = await api.post<CalibrationMeeting>(`/calibration-meetings/${id}/cancel/`);
    return response.data;
  },
};

// ============================================================
// Strategic Goals API
// ============================================================
export const goalsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<StrategicGoalList>>('/goals/', { params });
    return response.data;
  },

  get: async (id: number, params?: Record<string, any>) => {
    const response = await api.get<StrategicGoal>(`/goals/${id}/`, { params });
    return response.data;
  },

  create: async (data: Partial<StrategicGoal>) => {
    const response = await api.post<StrategicGoal>('/goals/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<StrategicGoal>) => {
    const response = await api.patch<StrategicGoal>(`/goals/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/goals/${id}/`);
    return response;
  },

  getInitiatives: async (id: number, params?: Record<string, any>) => {
    const response = await api.get<InitiativeList[]>(`/goals/${id}/initiatives/`, { params });
    return response.data;
  },

  getKPIs: async (id: number) => {
    const response = await api.get<KPIList[]>(`/goals/${id}/kpis/`);
    return response.data;
  },

  deletionImpact: (id: number) => api.get(`/goals/${id}/deletion-impact/`),
};

// ============================================================
// Goal Comments API
// ============================================================
export interface GoalComment {
  id: number;
  strategic_goal: number;
  parent_comment: number | null;
  comment_type: 'note' | 'question' | 'reply' | 'approval_note' | 'rejection_reason';
  comment_type_display: string;
  content: string;
  author: number;
  author_details: {
    id: number;
    username: string;
    full_name: string;
  };
  author_type: 'strategy_team' | 'org_employee';
  author_type_display: string;
  created_at: string;
  updated_at: string;
  is_read: boolean;
  is_resolved: boolean;
  is_edited: boolean;
  original_content: string;
  replies?: GoalComment[];
}

export const goalCommentsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<GoalComment[]>('/goal-comments/', { params });
    return response.data;
  },

  getForGoal: async (goalId: number) => {
    const response = await api.get<GoalComment[]>(`/goal-comments/for-goal/${goalId}/`);
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<GoalComment>(`/goal-comments/${id}/`);
    return response.data;
  },

  create: async (data: { strategic_goal: number; content: string; comment_type?: string; parent_comment?: number; author_type?: string }) => {
    const response = await api.post<GoalComment>('/goal-comments/', data);
    return response.data;
  },

  update: async (id: number, data: { content: string }) => {
    const response = await api.patch<GoalComment>(`/goal-comments/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/goal-comments/${id}/`);
    return response;
  },

  markRead: async (id: number) => {
    const response = await api.post<GoalComment>(`/goal-comments/${id}/mark-read/`);
    return response.data;
  },

  resolve: async (id: number) => {
    const response = await api.post<GoalComment>(`/goal-comments/${id}/resolve/`);
    return response.data;
  },

  getUnread: async () => {
    const response = await api.get<GoalComment[]>('/goal-comments/unread/');
    return response.data;
  },
};

// ============================================================
// KPI Comments API
// ============================================================
export interface KPIComment {
  id: number;
  kpi: number;
  kpi_name?: string;
  parent_comment: number | null;
  comment_type: 'note' | 'question' | 'reply';
  comment_type_display: string;
  content: string;
  author: number;
  author_details: {
    id: number;
    username: string;
    full_name: string;
  };
  author_type: 'strategy_team' | 'org_employee';
  author_type_display: string;
  created_at: string;
  updated_at: string;
  is_read: boolean;
  is_resolved: boolean;
  is_edited: boolean;
  original_content: string;
  replies?: KPIComment[];
}

export const kpiCommentsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<KPIComment[]>('/kpi-comments/', { params });
    return response.data;
  },

  getForKPI: async (kpiId: number) => {
    const response = await api.get<KPIComment[]>(`/kpi-comments/for-kpi/${kpiId}/`);
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<KPIComment>(`/kpi-comments/${id}/`);
    return response.data;
  },

  create: async (data: { kpi: number; content: string; comment_type?: string; parent_comment?: number; author_type?: string }) => {
    const response = await api.post<KPIComment>('/kpi-comments/', data);
    return response.data;
  },

  update: async (id: number, data: { content: string }) => {
    const response = await api.patch<KPIComment>(`/kpi-comments/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/kpi-comments/${id}/`);
    return response;
  },

  markRead: async (id: number) => {
    const response = await api.post<KPIComment>(`/kpi-comments/${id}/mark-read/`);
    return response.data;
  },

  resolve: async (id: number) => {
    const response = await api.post<KPIComment>(`/kpi-comments/${id}/resolve/`);
    return response.data;
  },

  getUnread: async () => {
    const response = await api.get<KPIComment[]>('/kpi-comments/unread/');
    return response.data;
  },
};

// ============================================================
// Initiative Comments API
// ============================================================
export interface InitiativeComment {
  id: number;
  initiative: number;
  initiative_title?: string;
  parent_comment: number | null;
  comment_type: 'note' | 'question' | 'reply';
  comment_type_display: string;
  content: string;
  author: number;
  author_details: {
    id: number;
    username: string;
    full_name: string;
  };
  author_type: 'strategy_team' | 'org_employee';
  author_type_display: string;
  created_at: string;
  updated_at: string;
  is_read: boolean;
  is_resolved: boolean;
  is_edited: boolean;
  original_content: string;
  replies?: InitiativeComment[];
}

export const initiativeCommentsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<InitiativeComment[]>('/initiative-comments/', { params });
    return response.data;
  },

  getForInitiative: async (initiativeId: number) => {
    const response = await api.get<InitiativeComment[]>(`/initiative-comments/for-initiative/${initiativeId}/`);
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<InitiativeComment>(`/initiative-comments/${id}/`);
    return response.data;
  },

  create: async (data: { initiative: number; content: string; comment_type?: string; parent_comment?: number; author_type?: string }) => {
    const response = await api.post<InitiativeComment>('/initiative-comments/', data);
    return response.data;
  },

  update: async (id: number, data: { content: string }) => {
    const response = await api.patch<InitiativeComment>(`/initiative-comments/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/initiative-comments/${id}/`);
    return response;
  },

  markRead: async (id: number) => {
    const response = await api.post<InitiativeComment>(`/initiative-comments/${id}/mark-read/`);
    return response.data;
  },

  resolve: async (id: number) => {
    const response = await api.post<InitiativeComment>(`/initiative-comments/${id}/resolve/`);
    return response.data;
  },

  getUnread: async () => {
    const response = await api.get<InitiativeComment[]>('/initiative-comments/unread/');
    return response.data;
  },
};

// ============================================================
// Activity Comments API
// ============================================================
export interface ActivityComment {
  id: number;
  activity: number;
  activity_name?: string;
  parent_comment: number | null;
  comment_type: 'note' | 'question' | 'reply';
  comment_type_display: string;
  content: string;
  author: number;
  author_details: {
    id: number;
    username: string;
    full_name: string;
  };
  author_type: 'strategy_team' | 'org_employee';
  author_type_display: string;
  created_at: string;
  updated_at: string;
  is_read: boolean;
  is_resolved: boolean;
  is_edited: boolean;
  original_content: string;
  replies?: ActivityComment[];
}

export const activityCommentsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<ActivityComment[]>('/activity-comments/', { params });
    return response.data;
  },

  getForActivity: async (activityId: number) => {
    const response = await api.get<ActivityComment[]>(`/activity-comments/for-activity/${activityId}/`);
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<ActivityComment>(`/activity-comments/${id}/`);
    return response.data;
  },

  create: async (data: { activity: number; content: string; comment_type?: string; parent_comment?: number; author_type?: string }) => {
    const response = await api.post<ActivityComment>('/activity-comments/', data);
    return response.data;
  },

  update: async (id: number, data: { content: string }) => {
    const response = await api.patch<ActivityComment>(`/activity-comments/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/activity-comments/${id}/`);
    return response;
  },

  markRead: async (id: number) => {
    const response = await api.post<ActivityComment>(`/activity-comments/${id}/mark-read/`);
    return response.data;
  },

  resolve: async (id: number) => {
    const response = await api.post<ActivityComment>(`/activity-comments/${id}/resolve/`);
    return response.data;
  },

  getUnread: async () => {
    const response = await api.get<ActivityComment[]>('/activity-comments/unread/');
    return response.data;
  },
};

// ============================================================
// Service Comments API
// ============================================================
export interface ServiceComment {
  id: number;
  service: number;
  service_name?: string;
  parent_comment: number | null;
  comment_type: 'note' | 'question' | 'reply';
  comment_type_display: string;
  content: string;
  author: number;
  author_details: {
    id: number;
    username: string;
    full_name: string;
  };
  author_type: 'strategy_team' | 'org_employee';
  author_type_display: string;
  created_at: string;
  updated_at: string;
  is_read: boolean;
  is_resolved: boolean;
  is_edited: boolean;
  original_content: string;
  replies?: ServiceComment[];
}

export const serviceCommentsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<ServiceComment[]>('/service-comments/', { params });
    return response.data;
  },

  getForService: async (serviceId: number) => {
    const response = await api.get<ServiceComment[]>(`/service-comments/for-service/${serviceId}/`);
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<ServiceComment>(`/service-comments/${id}/`);
    return response.data;
  },

  create: async (data: { service: number; content: string; comment_type?: string; parent_comment?: number; author_type?: string }) => {
    const response = await api.post<ServiceComment>('/service-comments/', data);
    return response.data;
  },

  update: async (id: number, data: { content: string }) => {
    const response = await api.patch<ServiceComment>(`/service-comments/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/service-comments/${id}/`);
    return response;
  },

  markRead: async (id: number) => {
    const response = await api.post<ServiceComment>(`/service-comments/${id}/mark-read/`);
    return response.data;
  },

  resolve: async (id: number) => {
    const response = await api.post<ServiceComment>(`/service-comments/${id}/resolve/`);
    return response.data;
  },

  getUnread: async () => {
    const response = await api.get<ServiceComment[]>('/service-comments/unread/');
    return response.data;
  },
};

// ============================================================
// Unit Goal Comments API
// ============================================================
export interface UnitGoalComment {
  id: number;
  unit_goal: number;
  unit_goal_name?: string;
  parent_comment: number | null;
  comment_type: 'note' | 'question' | 'reply';
  comment_type_display: string;
  content: string;
  author: number;
  author_details: {
    id: number;
    username: string;
    full_name: string;
  };
  author_type: 'strategy_team' | 'org_employee';
  author_type_display: string;
  created_at: string;
  updated_at: string;
  is_read: boolean;
  is_resolved: boolean;
  is_edited: boolean;
  original_content: string;
  replies?: UnitGoalComment[];
}

export const unitGoalCommentsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<UnitGoalComment[]>('/unit-goal-comments/', { params });
    return response.data;
  },

  getForUnitGoal: async (unitGoalId: number) => {
    const response = await api.get<UnitGoalComment[]>(`/unit-goal-comments/for-unit-goal/${unitGoalId}/`);
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<UnitGoalComment>(`/unit-goal-comments/${id}/`);
    return response.data;
  },

  create: async (data: { unit_goal: number; content: string; comment_type?: string; parent_comment?: number; author_type?: string }) => {
    const response = await api.post<UnitGoalComment>('/unit-goal-comments/', data);
    return response.data;
  },

  update: async (id: number, data: { content: string }) => {
    const response = await api.patch<UnitGoalComment>(`/unit-goal-comments/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/unit-goal-comments/${id}/`);
    return response;
  },

  markRead: async (id: number) => {
    const response = await api.post<UnitGoalComment>(`/unit-goal-comments/${id}/mark-read/`);
    return response.data;
  },

  resolve: async (id: number) => {
    const response = await api.post<UnitGoalComment>(`/unit-goal-comments/${id}/resolve/`);
    return response.data;
  },

  getUnread: async () => {
    const response = await api.get<UnitGoalComment[]>('/unit-goal-comments/unread/');
    return response.data;
  },
};

// ============================================================
// Initiatives API
// ============================================================
export const initiativesApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<InitiativeList>>('/initiatives/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<Initiative>(`/initiatives/${id}/`);
    return response.data;
  },

  create: async (data: Partial<Initiative>) => {
    const response = await api.post<Initiative>('/initiatives/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Initiative>) => {
    const response = await api.patch<Initiative>(`/initiatives/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/initiatives/${id}/`);
    return response;
  },

  getDashboard: async (id: number) => {
    const response = await api.get(`/initiatives/${id}/dashboard/`);
    return response.data;
  },

  bulkCreate: async (initiatives: Partial<Initiative>[]) => {
    const response = await api.post<Initiative[]>('/initiatives/bulk_create/', { initiatives });
    return response.data;
  },

  deletionImpact: (id: number) => api.get(`/initiatives/${id}/deletion-impact/`),

  // 3-stage approval workflow per DTS UC-03
  submitForApproval: async (id: number) => {
    const response = await api.post<Initiative>(`/initiatives/${id}/submit/`);
    return response.data;
  },
  directorApprove: async (id: number, comment?: string) => {
    const response = await api.post<Initiative>(
      `/initiatives/${id}/director-approve/`,
      { comment: comment || '' },
    );
    return response.data;
  },
  directorReject: async (id: number, comment: string) => {
    const response = await api.post<Initiative>(
      `/initiatives/${id}/director-reject/`,
      { comment },
    );
    return response.data;
  },
  strategyApprove: async (id: number, comment?: string) => {
    const response = await api.post<Initiative>(
      `/initiatives/${id}/strategy-approve/`,
      { comment: comment || '' },
    );
    return response.data;
  },
  strategyReject: async (id: number, comment: string) => {
    const response = await api.post<Initiative>(
      `/initiatives/${id}/strategy-reject/`,
      { comment },
    );
    return response.data;
  },
};

// ============================================================
// Activities API
// ============================================================
export const activitiesApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<ActivityList>>('/activities/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<Activity>(`/activities/${id}/`);
    return response.data;
  },

  create: async (data: Partial<Activity>) => {
    const response = await api.post<Activity>('/activities/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Activity>) => {
    const response = await api.patch<Activity>(`/activities/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/activities/${id}/`);
    return response;
  },

  // Get activities for a specific initiative
  getForInitiative: async (initiativeId: number) => {
    const response = await api.get<PaginatedResponse<ActivityList>>('/activities/', {
      params: { initiative: initiativeId }
    });
    return response.data;
  },

  deletionImpact: (id: number) => api.get(`/activities/${id}/deletion-impact/`),
};

// ============================================================
// KPIs API
// ============================================================
export const kpisApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<KPIList>>('/kpis/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<KPI>(`/kpis/${id}/`);
    return response.data;
  },

  create: async (data: Partial<KPI>) => {
    const response = await api.post<KPI>('/kpis/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<KPI>) => {
    const response = await api.patch<KPI>(`/kpis/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/kpis/${id}/`);
    return response;
  },

  getReadings: async (id: number, params?: Record<string, any>) => {
    const response = await api.get<KPIValue[]>(`/kpis/${id}/readings/`, { params });
    return response.data;
  },

  addReading: async (id: number, data: Partial<KPIValue>) => {
    const response = await api.post<KPIValue>(`/kpis/${id}/add_reading/`, data);
    return response.data;
  },

  getTrend: async (id: number) => {
    const response = await api.get(`/kpis/${id}/trend/`);
    return response.data;
  },

  getSchedules: async (id: number, params?: Record<string, any>) => {
    const response = await api.get<KPIReadingScheduleList[]>(`/kpis/${id}/schedules/`, { params });
    return response.data;
  },

  bulkCreate: async (kpis: Partial<KPI>[]) => {
    const response = await api.post<KPI[]>('/kpis/bulk_create/', { kpis });
    return response.data;
  },

  deletionImpact: (id: number) => api.get(`/kpis/${id}/deletion-impact/`),
};

// ============================================================
// KPI Values API
// ============================================================
export const kpiValuesApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<KPIValue>>('/kpi-values/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<KPIValue>(`/kpi-values/${id}/`);
    return response.data;
  },

  create: async (data: Partial<KPIValue>) => {
    const response = await api.post<KPIValue>('/kpi-values/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<KPIValue>) => {
    const response = await api.patch<KPIValue>(`/kpi-values/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/kpi-values/${id}/`);
    return response;
  },
};

// ============================================================
// Schedules API
// ============================================================
export const schedulesApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<KPIReadingScheduleList>>('/schedules/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<KPIReadingSchedule>(`/schedules/${id}/`);
    return response.data;
  },

  markComplete: async (id: number, value?: number, notes?: string) => {
    const response = await api.post<KPIReadingSchedule>(`/schedules/${id}/mark_complete/`, { value, notes });
    return response.data;
  },

  generate: async (kpiId: number, year?: number) => {
    const response = await api.post('/schedules/generate/', { kpi_id: kpiId, year });
    return response.data;
  },

  generateByOrg: async (organizationId: number, year?: number) => {
    const response = await api.post('/schedules/generate_by_org/', { organization_id: organizationId, year });
    return response.data;
  },

  orgSummary: async (params?: Record<string, any>) => {
    const response = await api.get('/schedules/org-summary/', { params });
    return response.data;
  },

  dueReadings: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<DueKPIReading>>('/schedules/due-readings/', { params });
    return response.data;
  },
};

// ============================================================
// Notifications API
// ============================================================
export const notificationsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<NotificationList>>('/notifications/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<Notification>(`/notifications/${id}/`);
    return response.data;
  },

  markRead: async (id: number) => {
    const response = await api.post<Notification>(`/notifications/${id}/mark_read/`);
    return response.data;
  },

  markAllRead: async () => {
    const response = await api.post<{ marked_read: number }>('/notifications/mark_all_read/');
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await api.get<{ unread_count: number }>('/notifications/unread_count/');
    return response.data;
  },
};

// ============================================================
// Users API
// ============================================================
export const usersApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<User>>('/users/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<User>(`/users/${id}/`);
    return response.data;
  },

  getMe: async () => {
    const response = await api.get<User>('/users/me/');
    return response.data;
  },

  // create/update/delete/updateProfile removed 2026-05-13 alongside
  // AdminScreen — user management lives in DTS-SSO. Only the
  // read-side methods (list/get/getMe) are kept for dashboards,
  // dropdown population and authentication state.
};

// ============================================================
// Dashboard API
// ============================================================
export const dashboardApi = {
  getData: async (year?: number) => {
    const params = year ? { year } : undefined;
    const response = await api.get<DashboardData>('/dashboard/', { params });
    return response.data;
  },

  getSettings: async () => {
    const response = await api.get<DashboardSettings>('/dashboard-settings/my_settings/');
    return response.data;
  },

  saveSettings: async (data: Partial<DashboardSettings>) => {
    const response = await api.post<DashboardSettings>('/dashboard-settings/save_settings/', data);
    return response.data;
  },

  getPerformance: async (orgId?: number) => {
    const url = orgId ? `/performance/${orgId}/` : '/performance/';
    const response = await api.get<PerformanceData>(url);
    return response.data;
  },
};

// ============================================================
// Services API
// ============================================================
export const servicesApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<ServiceList>>('/services/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<Service>(`/services/${id}/`);
    return response.data;
  },

  create: async (data: Partial<Service>) => {
    const response = await api.post<Service>('/services/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Service>) => {
    const response = await api.patch<Service>(`/services/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/services/${id}/`);
    return response;
  },

  deletionImpact: (id: number) => api.get(`/services/${id}/deletion-impact/`),
};

// ============================================================
// Organizational Units API
// ============================================================
export const organizationalUnitsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<OrganizationalUnitList>>('/organizational-units/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<OrganizationalUnit>(`/organizational-units/${id}/`);
    return response.data;
  },

  create: async (data: Partial<OrganizationalUnit>) => {
    const response = await api.post<OrganizationalUnit>('/organizational-units/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<OrganizationalUnit>) => {
    const response = await api.patch<OrganizationalUnit>(`/organizational-units/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/organizational-units/${id}/`);
    return response;
  },

  // Get strategic goals this unit is involved in
  strategicGoals: async (id: number) => {
    const response = await api.get<StrategicGoalList[]>(`/organizational-units/${id}/strategic-goals/`);
    return response.data;
  },

  // Get organizational units as hierarchical tree
  tree: async (params?: Record<string, any>) => {
    const response = await api.get<{ tree: any[]; primary_color: string; secondary_color: string }>('/organizational-units/tree/', { params });
    return response.data;
  },

  // Move a unit to a new parent with sibling reordering
  move: async (id: number, data: { new_parent_id: number | null; position: number }) => {
    const response = await api.post<{ tree: any[]; primary_color: string; secondary_color: string }>(`/organizational-units/${id}/move/`, data);
    return response.data;
  },

  deletionImpact: (id: number) => api.get(`/organizational-units/${id}/deletion-impact/`),
};

// ============================================================
// SSO Users API (proxy to SSO service)
// ============================================================
export interface SSOUserBrief {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  job_title?: string;
  position_type?: 'DG' | 'DIRECTOR' | 'MANAGER' | 'EMPLOYEE' | null;
  department_id?: number | null;
  organization?: { id: number; name: string } | null;
  is_active?: boolean;
  is_staff?: boolean;
  profile_photo?: string | null;
}

export const ssoUsersApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<SSOUserBrief>>('/sso-users/', { params });
    return response.data;
  },
  get: async (id: number) => {
    const response = await api.get<SSOUserBrief>(`/sso-users/${id}/`);
    return response.data;
  },
};

// ============================================================
// Employees API (الموظفين)
// ============================================================
export const employeesApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<Employee>>('/employees/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<Employee>(`/employees/${id}/`);
    return response.data;
  },

  create: async (data: Partial<Employee>) => {
    const response = await api.post<Employee>('/employees/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Employee>) => {
    const response = await api.patch<Employee>(`/employees/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/employees/${id}/`);
    return response;
  },

  byUnit: async (unitId: number) => {
    const response = await api.get<PaginatedResponse<Employee>>(`/employees/by-unit/${unitId}/`);
    return response.data;
  },

  uploadImage: async (id: number, formData: FormData) => {
    const response = await api.patch<Employee>(`/employees/${id}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get processes where employee is assigned to steps (via ManyToMany)
  processes: async (id: number) => {
    const response = await api.get<{
      employee: { id: number; name: string; position: string };
      processes: ProcessList[];
      total_processes: number;
    }>(`/employees/${id}/processes/`);
    return response.data;
  },

  // Import employees from Excel file
  importFromExcel: async (formData: FormData) => {
    const response = await api.post<{
      success: boolean;
      message: string;
      created: number;
      updated: number;
      errors: string[];
    }>('/employees/import-excel/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// ============================================================
// Unit Goals API (أهداف الوحدات التنظيمية)
// ============================================================
export const unitGoalsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<UnitGoalList>>('/unit-goals/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<UnitGoal>(`/unit-goals/${id}/`);
    return response.data;
  },

  create: async (data: Partial<UnitGoal>) => {
    const response = await api.post<UnitGoal>('/unit-goals/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<UnitGoal>) => {
    const response = await api.patch<UnitGoal>(`/unit-goals/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/unit-goals/${id}/`);
    return response;
  },

  deletionImpact: (id: number) => api.get(`/unit-goals/${id}/deletion-impact/`),

  importFromExcel: async (formData: FormData) => {
    const response = await api.post<{
      success: boolean;
      message: string;
      created: number;
      updated: number;
      errors: string[];
    }>('/unit-goals/import-excel/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  downloadTemplate: async (lang: string = 'ar') => {
    const response = await api.get('/unit-goals/download-template/', {
      responseType: 'blob',
      params: { lang },
    });
    return response;
  },
};

// ============================================================
// Processes API (العمليات)
// ============================================================
export const processesApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<ProcessList>>('/processes/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<Process>(`/processes/${id}/`);
    return response.data;
  },

  create: async (data: Partial<Process>) => {
    const response = await api.post<Process>('/processes/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Process>) => {
    const response = await api.patch<Process>(`/processes/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/processes/${id}/`);
    return response;
  },

  deletionImpact: (id: number) => api.get(`/processes/${id}/deletion-impact/`),

  downloadTemplate: async (lang: string = 'ar') => {
    const response = await api.get('/processes/download-template/', {
      responseType: 'blob',
      params: { lang },
    });
    return response;
  },

  importFromExcel: async (formData: FormData) => {
    const response = await api.post<{
      success: boolean;
      message: string;
      created: number;
      updated: number;
      errors: string[];
    }>('/processes/import-excel/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// ============================================================
// SubProcesses API (العمليات الفرعية)
// ============================================================
export const subProcessesApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<SubProcess>>('/sub-processes/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<SubProcess>(`/sub-processes/${id}/`);
    return response.data;
  },

  create: async (data: Partial<SubProcess> | FormData) => {
    // When data is FormData, let axios set Content-Type automatically with boundary
    const response = await api.post<SubProcess>('/sub-processes/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<SubProcess> | FormData) => {
    // When data is FormData, let axios set Content-Type automatically with boundary
    const response = await api.patch<SubProcess>(`/sub-processes/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/sub-processes/${id}/`);
    return response;
  },

  deletionImpact: (id: number) => api.get(`/sub-processes/${id}/deletion-impact/`),
};

// ============================================================
// Process Steps API (خطوات العملية)
// ============================================================
export const processStepsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<ProcessStep>>('/process-steps/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<ProcessStep>(`/process-steps/${id}/`);
    return response.data;
  },

  create: async (data: Partial<ProcessStep>) => {
    const response = await api.post<ProcessStep>('/process-steps/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<ProcessStep>) => {
    const response = await api.patch<ProcessStep>(`/process-steps/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/process-steps/${id}/`);
    return response;
  },
};

// ============================================================
// Operations API (العمليات — leaf under SubProcess)
// ============================================================
export const operationsApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<Operation>>('/operations/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<Operation>(`/operations/${id}/`);
    return response.data;
  },

  create: async (data: Partial<Operation>) => {
    const response = await api.post<Operation>('/operations/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Operation>) => {
    const response = await api.patch<Operation>(`/operations/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/operations/${id}/`);
    return response;
  },
};

// ============================================================
// Risks API
// ============================================================
export const risksApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<any>>('/risks/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<any>(`/risks/${id}/`);
    return response.data;
  },

  create: async (data: Partial<any>) => {
    const response = await api.post<any>('/risks/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<any>) => {
    const response = await api.patch<any>(`/risks/${id}/`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/risks/${id}/`);
    return response;
  },

  forItem: async (relatedType: string, relatedId: number) => {
    // Backend action is registered at `risks/for/<type>/<id>/` (RiskViewSet
    // @action url_path='for/...'), NOT 'for-item'.
    const response = await api.get<any[]>(`/risks/for/${relatedType}/${relatedId}/`);
    return response.data;
  },

  statistics: async () => {
    const response = await api.get<any>('/risks/statistics/');
    return response.data;
  },
};

// ============================================================
// Evidence API
// ============================================================
export interface Evidence {
  id: number;
  related_type: 'kpi' | 'kpi_value' | 'strategic_goal' | 'initiative' | 'process' | 'service' | 'activity' | 'employee';
  related_id: number;
  evidence_type: 'image' | 'file' | 'text' | 'link';
  title: string;
  description?: string;
  file?: string;
  text_content?: string;
  link_url?: string;
  uploaded_by?: number;
  created_at?: string;
}

export const evidenceApi = {
  list: async (params?: Record<string, any>) => {
    const response = await api.get<PaginatedResponse<Evidence>>('/evidence/', { params });
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<Evidence>(`/evidence/${id}/`);
    return response.data;
  },

  create: async (data: FormData) => {
    const response = await api.post<Evidence>('/evidence/', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  forObject: async (type: string, id: number) => {
    const response = await api.get<Evidence[]>(`/evidence/for-object/`, {
      params: { type, id }
    });
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/evidence/${id}/`);
    return response;
  },
};

// ============================================================
// Change Requests API (طلبات التعديل/الحذف)
// ============================================================
export const changeRequestsApi = {
  list: (params?: Record<string, any>) =>
    api.get('/change-requests/', { params }),
  get: (id: number) =>
    api.get(`/change-requests/${id}/`),
  approve: (id: number) =>
    api.post(`/change-requests/${id}/approve/`),
  reject: (id: number, reason: string) =>
    api.post(`/change-requests/${id}/reject/`, { reason }),
  cancel: (id: number) =>
    api.post(`/change-requests/${id}/cancel/`),
  bulkApprove: (ids: number[]) =>
    api.post('/change-requests/bulk-approve/', { ids }),
  bulkReject: (ids: number[], reason: string) =>
    api.post('/change-requests/bulk-reject/', { ids, reason }),
};

// ============================================================
// Global Search API (البحث العام)
// ============================================================
export interface SearchResults {
  query: string;
  total_count: number;
  results: {
    strategic_goals: {
      count: number;
      items: Array<{
        id: number;
        name: string;
        description: string;
        organization__name: string;
      }>;
    };
    initiatives: {
      count: number;
      items: Array<{
        id: number;
        name: string;
        description: string;
        organization__name: string;
        strategic_goal__name: string;
      }>;
    };
    activities: {
      count: number;
      items: Array<{
        id: number;
        name: string;
        description: string;
        organization__name: string;
        initiative__name: string;
      }>;
    };
    kpis: {
      count: number;
      items: Array<{
        id: number;
        name: string;
        description: string;
        organization__name: string;
        source_type: string;
      }>;
    };
    processes: {
      count: number;
      items: Array<{
        id: number;
        name: string;
        description: string;
        organization__name: string;
      }>;
    };
    sub_processes: {
      count: number;
      items: Array<{
        id: number;
        name: string;
        description: string;
        organization__name: string;
        process__name: string;
      }>;
    };
    services: {
      count: number;
      items: Array<{
        id: number;
        name: string;
        description: string;
        organization__name: string;
      }>;
    };
    unit_goals: {
      count: number;
      items: Array<{
        id: number;
        name: string;
        description: string;
        organization__name: string;
        unit__name: string;
      }>;
    };
    risks: {
      count: number;
      items: Array<{
        id: number;
        name: string;
        description: string;
        organization__name: string;
        level: string;
      }>;
    };
  };
}

export const searchApi = {
  search: async (query: string) => {
    const response = await api.get<SearchResults>('/search/', {
      params: { q: query }
    });
    return response.data;
  },
};
